import { create } from "zustand";

export type AlertType = "success" | "error" | "warning" | "info";

export type AlertPosition = "desktop" | "mobile";

export interface Alert {
  id: string;
  type: AlertType;
  title: string;
  message?: string;
  position?: AlertPosition;
  duration?: number; // in milliseconds, 0 means no auto-dismiss
  showIcon?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface AlertStore {
  alerts: Alert[];
  addAlert: (alert: Omit<Alert, "id">) => string;
  removeAlert: (id: string) => void;
  clearAlerts: () => void;
  showSuccess: (
    title: string,
    message?: string,
    options?: Partial<Alert>
  ) => string;
  showError: (
    title: string,
    message?: string,
    options?: Partial<Alert>
  ) => string;
  showWarning: (
    title: string,
    message?: string,
    options?: Partial<Alert>
  ) => string;
  showInfo: (
    title: string,
    message?: string,
    options?: Partial<Alert>
  ) => string;
}

export const useAlertStore = create<AlertStore>((set, get) => ({
  alerts: [],

  addAlert: (alert) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newAlert: Alert = {
      id,
      position: "desktop",
      duration: 5000,
      showIcon: true,
      ...alert,
    };

    set((state) => ({
      alerts: [...state.alerts, newAlert],
    }));

    // Auto-dismiss if duration is set
    if (newAlert.duration && newAlert.duration > 0) {
      setTimeout(() => {
        get().removeAlert(id);
      }, newAlert.duration);
    }

    return id;
  },

  removeAlert: (id) => {
    set((state) => ({
      alerts: state.alerts.filter((alert) => alert.id !== id),
    }));
  },

  clearAlerts: () => {
    set({ alerts: [] });
  },

  showSuccess: (title, message, options = {}) => {
    return get().addAlert({
      type: "success",
      title,
      message,
      ...options,
    });
  },

  showError: (title, message, options = {}) => {
    return get().addAlert({
      type: "error",
      title,
      message,
      ...options,
    });
  },

  showWarning: (title, message, options = {}) => {
    return get().addAlert({
      type: "warning",
      title,
      message,
      ...options,
    });
  },

  showInfo: (title, message, options = {}) => {
    return get().addAlert({
      type: "info",
      title,
      message,
      ...options,
    });
  },
}));
